import { giphyBlue, giphyCharcoal, giphyDarkestGrey, giphyLightGrey } from '@giphy/js-brand'
import { Button, Input } from '@giphy/react-giphy-brand'
import React, { useContext, useState } from 'react'
import MessageContext from 'shared/components/context/message'
import FileInput from 'shared/components/inputs/file-input'
import DotsLoader from 'shared/components/loaders/dots'
import { googleAnalyticsEvent } from 'shared/util/analytics'
import { URL_TYPE_MAPPER } from 'shared/util/gif-maker-util'
import styled from 'styled-components'
import Modal from '../shared/components/modal'
import VimeoIcon from '../shared/components/vimeo-icon'
import YouTubeIcon from '../shared/components/youtube-icon'
import API from '../shared/utils/api'

const VIDEO_MIME_TYPES = [
    ...[`mp4`, `mpg`, `mpeg`, `m4v`, `x-m4v`, `mov`, `quicktime`].map(t => `video/${t}`),
    `video/*`,
]

const api = new API()

const HeadingContainer = styled.div`
    padding: 150px 0 30px 0;
`

const Heading = styled.div`
    font-weight: bold;
    font-size: 18px;
    letter-spacing: 0.26px;
    margin-bottom: 7px;
`

const HeadingSubtext = styled.div`
    display: inline;
`

const HeadingCTA = styled.div`
    float: right;
    a {
        border-bottom: #00ccff 0.125em solid;
    }
`

const StyledFileInput = styled(FileInput)`
    position: relative;
    float: left;
    height: 50px;
    width: 300px;

    & input[type='file'] {
        height: 100%;
        left: 0;
        opacity: 0;
        position: absolute;
        top: 0;
        width: 100%;
        cursor: pointer;
    }
`

const UploadButton = styled(Button)`
    height: 100%;
    width: 100%;
`

const UrlInputContainer = styled.div`
    width: calc(100% - 310px);
    height: 50px;
    margin-left: 10px;
    position: relative;
    display: inline-block;
`

const UrlInput = styled(Input)`
    margin: 0;
    height: 50px;
`

const UrlInputIcons = styled.div`
    position: absolute;
    top: 50%;
    right: 0;
    transform: translateY(-50%);
    height: 30px;
`

const Icon = styled.div`
    display: inline-block;
    margin-right: 5px;
`

const TitleInput = styled(Input)``

const Label = styled.div`
    color: ${giphyLightGrey};
    font-size: 14px;
    font-weight: bold;
`

const ModalButtons = styled.div`
    margin-top: 100px;
    text-align: center;
`

const BeginCaptureButton = styled(Button)`
    display: block;
    margin: 0 auto 15px auto;
    width: 500px;
`

const CancelButton = styled(Button)`
    display: block;
    margin: auto;
    background-color: ${giphyDarkestGrey};
    color: ${giphyLightGrey};
`

const TabsContainer = styled.div`
    border-bottom: 1px solid ${giphyCharcoal};
    width: 100%;
`

const Tab = styled.div`
    font-family: 'NexaBlack';
    font-size: 16px;
    display: inline-block;
    border-bottom: 4px solid ${giphyBlue};
    height: 35px;
`

const ContentContainer = styled.div`
    padding: 30px 0;
`

const LoaderContainer = styled.div`
    text-align: center;
    margin-top: 150px;
`

const LoaderText = styled.div`
    margin-top: 25px;
    font-size: 14px;
    font-weight: bold;
`

const StyledModal = styled(Modal)`
    border-radius: 4px 4px 0 0;
`

const ModalInner = styled.div`
    padding: 20px;
`

const Warning = styled.div`
    background: linear-gradient(-135deg, rgb(255, 102, 102) 0%, rgb(153, 51, 255) 100%);
    height: 50px;
    line-height: 52px;
    text-align: center;
    font-size: 15px;
    border-radius: 4px 4px 0 0;
`

const AlertIcon = styled.div`
    display: inline-block;
    margin-right: 20px;
    vertical-align: top;
`

type Props = {
    onCaptureAdded?: (uuid: string) => void
}

const NewCapture = ({ onCaptureAdded = () => {} }: Props) => {
    const { sendMessage } = useContext(MessageContext)
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [isUploading, setIsUploading] = useState(false)
    const [title, setTitle] = useState('')
    const [url, setUrl] = useState('')
    const [file, setFile] = useState(null as File | null)
    const [fileUploadProgress, setFileUploadProgress] = useState<number>()

    const reset = () => {
        setIsUploading(false)
        setFileUploadProgress(undefined)
        setIsModalOpen(false)
        setTitle('')
        setUrl('')
        setFile(null)
    }

    const onBeginClicked = async () => {
        if (file || url) {
            setIsUploading(true)

            try {
                const { uuid } = file
                    ? await api.newCaptureFromFile(title, file as File, p => setFileUploadProgress(p))
                    : await api.newCaptureFromUrl(title, url)

                if (uuid) {
                    googleAnalyticsEvent({
                        category: 'Super Cut',
                        action: 'Capture Start',
                        label: '',
                    })
                    onCaptureAdded(uuid)
                } else {
                    throw 'Unexpected response from API'
                }
            } catch (e) {
                sendMessage('There was a problem starting your capture', 'error')
            } finally {
                reset()
            }
        }
    }

    const onCancelClicked = () => {
        reset()
    }

    const onUrlChanged = async (evt: React.ChangeEvent<HTMLInputElement>) => {
        const url = evt.target.value
        setUrl(url)
        const urlType = URL_TYPE_MAPPER(url)
        if (['Youtube', 'Vimeo'].indexOf(urlType) > -1) {
            googleAnalyticsEvent({
                category: 'Super Cut',
                action: 'Input',
                label: urlType,
            })
            const videoInfo = await api.getExternalVideoInfo(url)
            if (videoInfo && videoInfo.title) {
                setTitle(videoInfo.title)
            }
            setIsModalOpen(true)
        }
    }

    const onFileAccepted = data => {
        googleAnalyticsEvent({
            category: 'Super Cut',
            action: 'Input',
            label: 'Browse Files',
        })
        const file = data[0].file
        setFile(file)
        setTitle(file.name)
        setIsModalOpen(true)
    }

    const onTitleChanged = (evt: React.ChangeEvent<HTMLInputElement>) => {
        const title = evt.target.value
        setTitle(title)
    }
    return (
        <div>
            <HeadingContainer>
                <Heading>New Capture</Heading>
                <HeadingSubtext>We support your computer, YouTube, Vimeo, and many others!</HeadingSubtext>
                <HeadingCTA>
                    Need help? Try our <a href="https://support.giphy.com/hc/en-us/articles/360049349132">FAQ</a>.
                </HeadingCTA>
            </HeadingContainer>

            <div>
                <StyledFileInput
                    isLoggedIn={true}
                    acceptMultiple={false}
                    maxDuration={7200}
                    maxFilesize={5000}
                    mimeTypes={VIDEO_MIME_TYPES}
                    onFileAccepted={onFileAccepted}
                    onError={(_, message) => sendMessage(message, 'error')}
                >
                    <UploadButton>Upload from Computer</UploadButton>
                </StyledFileInput>
                <UrlInputContainer>
                    <UrlInput inputProps={{ value: url, placeholder: 'Enter any media URL', onChange: onUrlChanged }} />
                    <UrlInputIcons>
                        <Icon>
                            <YouTubeIcon />
                        </Icon>
                        <Icon>
                            <VimeoIcon />
                        </Icon>
                    </UrlInputIcons>
                </UrlInputContainer>
            </div>

            <StyledModal isOpen={isModalOpen} onClickOutside={() => !isUploading && setIsModalOpen(false)}>
                <Warning>
                    <AlertIcon className="ss-alert" />
                    Please only upload video that you own. Upgraded channels hosting unrelated content can be
                    downgraded.
                </Warning>
                <ModalInner>
                    <TabsContainer>
                        <Tab>New Capture</Tab>
                    </TabsContainer>
                    <ContentContainer>
                        {isUploading ? (
                            <>
                                <LoaderContainer>
                                    <DotsLoader />
                                    <LoaderText>
                                        Uploading your video
                                        {fileUploadProgress !== undefined &&
                                            ` (${(fileUploadProgress * 100).toFixed(2)}%)`}
                                    </LoaderText>
                                </LoaderContainer>
                            </>
                        ) : (
                            <>
                                <Label>Title</Label>
                                <TitleInput
                                    inputProps={{
                                        value: title,
                                        placeholder: 'Enter a title',
                                        onChange: onTitleChanged,
                                    }}
                                />
                            </>
                        )}
                        <ModalButtons>
                            <BeginCaptureButton disabled={isUploading || !title} onClick={onBeginClicked}>
                                Begin Capture
                            </BeginCaptureButton>
                            <CancelButton onClick={onCancelClicked}>Cancel</CancelButton>
                        </ModalButtons>
                    </ContentContainer>
                </ModalInner>
            </StyledModal>
        </div>
    )
}

export default NewCapture
